import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bannersService } from "@/services/bannersService";
import { cmsService } from "@/services/cmsService";
import { couponsService } from "@/services/couponsService";
import type { BannerCreateDto, BannerFilterParams, BannerUpdateDto } from "@/types/banner.dto";
import type { CmsPageCreateDto, CmsPageUpdateDto } from "@/types/cms.dto";
import type { CouponCreateDto, CouponFilterParams, CouponUpdateDto } from "@/types/coupon.dto";
import { queryKeys } from "./keys";

export function useAdminBanners(filter: BannerFilterParams = { activeOnly: false, pageSize: 100 }) {
  return useQuery({
    queryKey: queryKeys.banners.admin(filter),
    queryFn: () => bannersService.getAll(filter),
  });
}

export function useCreateBannerMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: BannerCreateDto) => bannersService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["banners"] });
    },
  });
}

export function useUpdateBannerMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: BannerUpdateDto }) => bannersService.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["banners"] });
    },
  });
}

export function useDeleteBannerMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => bannersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["banners"] });
    },
  });
}

export function useAdminCmsPages(page = 1, pageSize = 50) {
  return useQuery({
    queryKey: queryKeys.cms.admin(page, pageSize),
    queryFn: () => cmsService.getAll(page, pageSize, false),
  });
}

export function useCreateCmsPageMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CmsPageCreateDto) => cmsService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cms"] });
    },
  });
}

export function useUpdateCmsPageMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: CmsPageUpdateDto }) => cmsService.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cms"] });
    },
  });
}

export function useDeleteCmsPageMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cmsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cms"] });
    },
  });
}

export function useAdminCoupons(filter: CouponFilterParams = { pageSize: 100 }) {
  return useQuery({
    queryKey: queryKeys.coupons.admin(filter),
    queryFn: () => couponsService.getAll(filter),
  });
}

export function useCreateCouponMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CouponCreateDto) => couponsService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coupons"] });
    },
  });
}

export function useUpdateCouponMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: CouponUpdateDto }) => couponsService.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coupons"] });
    },
  });
}

export function useDeleteCouponMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => couponsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coupons"] });
    },
  });
}
